import React, { useState } from 'react';
import { Autocomplete, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { ConfirmDialog } from '../../reports/edit/EditCommon';
import { EntryDateField, UntilField } from './EntryRuleFields';
import {
  KIND_HINTS, KIND_LABELS, fromInputMoment, pharmacyLabel, toInputMoment, validateDate, validateUntil
} from './revenueEntryCalc';
import type { EntryData, EntryKind, EntryPharmacy, EntryRuleInput } from './revenueEntryApi';

interface EntryPersonalDialogProps {
  open: boolean;
  /** Виды исключений, доступные оператору (продление и разблокировка - одно право, дата - другое) */
  kinds: EntryKind[];
  /** Данные экрана с аптеками зоны; форма заполняется при монтировании: на каждое открытие свой key */
  data: EntryData;
  pharmacies: EntryPharmacy[];
  saving: boolean;
  /** Возвращает true, если исключения сохранены и форму можно закрыть */
  onSave: (pharmacies: string[], kind: EntryKind, input: EntryRuleInput) => Promise<boolean>;
  onClose: () => void;
}

const PERSONAL_HINTS: Record<EntryKind, string> = {
  ...KIND_HINTS,
  extend: `Продлевается текущая дата ввода каждой выбранной аптеки. ${KIND_HINTS.extend}`
};

/** Одно исключение сразу для одной или нескольких аптек зоны; прежнее исключение того же вида заменяется */
const EntryPersonalDialog: React.FC<EntryPersonalDialogProps> = ({ open, kinds, data, pharmacies, saving, onSave, onClose }) => {
  const { bounds, state } = data;
  const [kind, setKind] = useState<EntryKind>(kinds[0]);
  const [picked, setPicked] = useState<EntryPharmacy[]>([]);
  const [until, setUntil] = useState(toInputMoment(bounds.defaults[kinds[0]]));
  const [date, setDate] = useState(state.date);
  const [errors, setErrors] = useState({ picked: '', until: '', date: '' });

  // Срок по умолчанию у каждого вида свой: при смене вида он подставляется заново
  const changeKind = (next: EntryKind) => {
    setKind(next);
    setUntil(toInputMoment(bounds.defaults[next]));
    setErrors({ picked: '', until: '', date: '' });
  };

  const submit = async () => {
    if (saving) return;
    const found = {
      picked: picked.length === 0 ? 'Выберите хотя бы одну аптеку' : '',
      until: validateUntil(until, bounds, kind),
      date: kind === 'date' ? validateDate(date, bounds) : ''
    };
    setErrors(found);
    if (found.picked || found.until || found.date) return;

    const input: EntryRuleInput = kind === 'date' ? { until: fromInputMoment(until), date } : { until: fromInputMoment(until) };
    if (await onSave(picked.map((item) => item.username), kind, input)) onClose();
  };

  const allPicked = picked.length === pharmacies.length;

  return (
    <ConfirmDialog
      open={open}
      title="Исключение для аптек"
      confirmText={picked.length > 1 ? `Сохранить для ${picked.length} аптек` : 'Сохранить'}
      loading={saving}
      onConfirm={submit}
      onClose={onClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          select
          size="small"
          fullWidth
          label="Параметр"
          value={kind}
          onChange={(event) => changeKind(event.target.value as EntryKind)}
        >
          {kinds.map((item) => (
            <MenuItem key={item} value={item}>
              {KIND_LABELS[item]}
            </MenuItem>
          ))}
        </TextField>
        <Typography sx={{ color: '#64748b', fontSize: '0.86rem', mt: -1 }}>{PERSONAL_HINTS[kind]}</Typography>

        <Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={6}
            options={pharmacies}
            value={picked}
            onChange={(_, next) => {
              setPicked(next);
              setErrors((prev) => ({ ...prev, picked: '' }));
            }}
            getOptionLabel={pharmacyLabel}
            isOptionEqualToValue={(a, b) => a.username === b.username}
            noOptionsText="Аптеки не найдены"
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Аптеки"
                placeholder={picked.length === 0 ? 'Номер или логин' : undefined}
                error={!!errors.picked}
                helperText={errors.picked || `Выбрано: ${picked.length} из ${pharmacies.length}`}
              />
            )}
          />
          <Button
            size="small"
            onClick={() => {
              setPicked(allPicked ? [] : pharmacies);
              setErrors((prev) => ({ ...prev, picked: '' }));
            }}
            sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600 }}
          >
            {allPicked ? 'Снять выбор' : 'Выбрать все аптеки'}
          </Button>
        </Box>

        {kind === 'date' && (
          <EntryDateField
            value={date}
            onChange={(value) => {
              setDate(value);
              setErrors((prev) => ({ ...prev, date: '' }));
            }}
            bounds={bounds}
            error={errors.date}
          />
        )}
        <UntilField
          value={until}
          onChange={(value) => {
            setUntil(value);
            setErrors((prev) => ({ ...prev, until: '' }));
          }}
          bounds={bounds}
          kind={kind}
          error={errors.until}
        />
      </Box>
    </ConfirmDialog>
  );
};

export default EntryPersonalDialog;
